// Offers page: every affiliate offer across the creator's profiles, with
// Active / Expired / Deactivated tabs, grid and list views, and Group by Brand
// (EPD-12475: EPD-12459, EPD-12460, EPD-12461).

import {
  CampaignsEmpty,
  GroupByMenu,
  ViewToggle,
  type CampaignView,
  type GroupBy,
} from "@/components/campaigns/campaigns-toolbar";
import {
  BrandLogosProvider,
  BrandLogoAvatar,
} from "@/components/brand/brand-logos";
import {
  OfferBrandGrid,
  OfferBrandSummary,
  OfferBrandTable,
} from "@/components/offers/offer-brand-groups";
import { OfferCard } from "@/components/offers/offer-card";
import { OfferStatusTabs } from "@/components/offers/offer-status-tabs";
import { OfferTable } from "@/components/offers/offer-table";
import { offerBrandImages } from "@/lib/offers/brand-images";
import { offerKey, plural } from "@/lib/offers/display";
import {
  openOfferBrand,
  parseOffersSearch,
  type OffersSearch,
} from "@/lib/offers/search";
import { groupOffersByBrand } from "@/lib/offers/sort";
import { OFFER_TABS, OFFER_TAB_LABELS, type OfferTab } from "@/lib/offers/tabs";
import {
  useCreatorAffiliateOffersQuery,
  type CreatorAffiliateOffer,
} from "@/lib/queries/promo-codes";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import { Alert, AlertDescription, Card, CardContent, Skeleton } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";
import type { OrgRouteContext } from "./route";

export const Route = createFileRoute("/(org)/o/$orgSlug/offers/")({
  validateSearch: (search: Record<string, unknown>): OffersSearch =>
    parseOffersSearch(search),
  component: Offers,
});

const OFFER_GRID =
  "grid gap-4 md:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] md:gap-5";

function Offers() {
  const { organization } = Route.useRouteContext() as OrgRouteContext;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const organizationId = organization?.id ?? "";

  const { data, isPending, error } =
    useCreatorAffiliateOffersQuery(organizationId);
  const offers = useMemo(() => data?.offers ?? [], [data]);
  // Some profiles or brands couldn't be read, so the counts may be short.
  const incomplete = data?.incomplete ?? false;

  const tab: OfferTab = search.tab ?? "active";
  const view: CampaignView = search.view ?? "grid";
  // Not in the URL, so every visit starts on Brand (EPD-12586).
  const [groupBy, setGroupBy] = useState<GroupBy>("brand");

  const counts = useMemo(
    () =>
      Object.fromEntries(
        OFFER_TABS.map((key) => [
          key,
          offers.filter((offer) => offer.tab === key).length,
        ]),
      ) as Record<OfferTab, number>,
    [offers],
  );
  const tabOffers = useMemo(
    () => offers.filter((offer) => offer.tab === tab),
    [offers, tab],
  );
  // From every tab, so brand cards keep their image when the tab changes
  // (EPD-12612).
  const coverImages = useMemo(() => offerBrandImages(offers), [offers]);
  const logoClientIds = useMemo(
    () => offers.map((offer) => offer.clientId),
    [offers],
  );

  const updateSearch = (patch: OffersSearch) =>
    navigate({
      to: ".",
      search: (prev) => parseOffersSearch({ ...prev, ...patch }),
    });
  const setView = (next: CampaignView) =>
    updateSearch({ view: next === "list" ? "list" : undefined });

  if (!ensureOrgContext(organization, "offers.index.tsx#Offers")) {
    return null;
  }

  if (isPending) return <OffersSkeleton />;

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Failed to load offers: {error.message}
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabLabel = OFFER_TAB_LABELS[tab].toLowerCase();
  const brand = openOfferBrand(tabOffers, search);

  if (brand) {
    const brandOffers = tabOffers.filter(
      (offer) => offer.clientId === brand.clientId,
    );
    return (
      <BrandLogosProvider
        organizationId={organization.id}
        clientIds={logoClientIds}
      >
        <div className="space-y-6 p-4 sm:px-7 sm:py-8">
          <button
            type="button"
            onClick={() => updateSearch({ brand: undefined })}
            className="inline-flex items-center gap-2 text-sm font-medium hover:text-muted-foreground"
          >
            <ArrowLeft className="size-4" aria-hidden />
            All Brands
          </button>

          <div className="flex items-center gap-4">
            <div className="grid size-[72px] shrink-0 place-content-center rounded-xl border bg-card">
              <BrandLogoAvatar
                clientId={brand.clientId}
                name={brand.clientName}
                size="lg"
                className="rounded-lg"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <h1 className="truncate text-2xl font-bold tracking-tight sm:text-[30px]">
                {brand.clientName}
              </h1>
              <span className="text-sm text-muted-foreground">
                {plural(brandOffers.length, `${tabLabel} offer`)}
              </span>
            </div>
          </div>

          <div className="flex justify-end">
            <ViewToggle view={view} onChange={setView} />
          </div>

          {incomplete && <IncompleteNotice />}

          <OfferList
            offers={brandOffers}
            view={view}
            orgSlug={organization.slug}
            showBrand={false}
          />
        </div>
      </BrandLogosProvider>
    );
  }

  return (
    <BrandLogosProvider
      organizationId={organization.id}
      clientIds={logoClientIds}
    >
      <div className="space-y-6 p-4 sm:px-7 sm:py-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-[30px]">
            Offers
          </h1>
          <p className="mt-2 text-[15px] text-muted-foreground">
            View discount codes and affiliate links across all your creator
            profiles
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <OfferStatusTabs
            tab={tab}
            counts={counts}
            onSelect={(next) =>
              updateSearch({
                tab: next === "active" ? undefined : next,
                brand: undefined,
              })
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <ViewToggle view={view} onChange={setView} />
            <GroupByMenu
              groupBy={groupBy}
              itemLabel="Offer"
              onChange={(next) => {
                setGroupBy(next);
                if (search.brand) updateSearch({ brand: undefined });
              }}
            />
          </div>
        </div>

        {incomplete && <IncompleteNotice />}

        {tabOffers.length === 0 ? (
          <CampaignsEmpty
            title={`No ${tabLabel} offers`}
            body="Offers will show here when they move to this status."
          />
        ) : groupBy === "brand" ? (
          <BrandGroups
            offers={tabOffers}
            view={view}
            orgSlug={organization.slug}
            coverImages={coverImages}
          />
        ) : (
          <OfferList
            offers={tabOffers}
            view={view}
            orgSlug={organization.slug}
          />
        )}
      </div>
    </BrandLogosProvider>
  );
}

function OfferList({
  offers,
  view,
  orgSlug,
  showBrand = true,
}: {
  offers: CreatorAffiliateOffer[];
  view: CampaignView;
  orgSlug: string;
  showBrand?: boolean;
}) {
  if (view === "list") {
    return (
      <OfferTable offers={offers} orgSlug={orgSlug} showBrand={showBrand} />
    );
  }
  return (
    <div className={OFFER_GRID}>
      {offers.map((offer) => (
        <OfferCard
          key={offerKey(offer)}
          offer={offer}
          orgSlug={orgSlug}
          showBrand={showBrand}
        />
      ))}
    </div>
  );
}

function BrandGroups({
  offers,
  view,
  orgSlug,
  coverImages,
}: {
  offers: CreatorAffiliateOffer[];
  view: CampaignView;
  orgSlug: string;
  coverImages: ReadonlyMap<string, string>;
}) {
  const groups = useMemo(() => groupOffersByBrand(offers), [offers]);
  return (
    <div className="space-y-4">
      <OfferBrandSummary
        brandCount={groups.length}
        offerCount={offers.length}
      />
      {view === "list" ? (
        <OfferBrandTable groups={groups} orgSlug={orgSlug} />
      ) : (
        <OfferBrandGrid
          groups={groups}
          orgSlug={orgSlug}
          coverImages={coverImages}
        />
      )}
    </div>
  );
}

function IncompleteNotice() {
  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>Some offers couldn't be loaded.</AlertDescription>
    </Alert>
  );
}

function OffersSkeleton() {
  return (
    <div className="space-y-6 p-4 sm:px-7 sm:py-8">
      <div className="space-y-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <Skeleton className="h-10 w-80 max-w-full" />
      <div className={OFFER_GRID}>
        {["a", "b", "c"].map((key) => (
          <Skeleton key={key} className="h-52 rounded-xl" />
        ))}
      </div>
    </div>
  );
}
